// ============================================
// API Service - Simplified (No Auth)
// ============================================

import axios, { AxiosInstance } from 'axios';
import { FormSubmission } from '../types';
import { getDashboardToken, isSessionTokenExpired, refreshSessionToken } from '../utils/session';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api'
// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for PDF generation
});

// Request interceptor: attach token and auto-refresh if expired
api.interceptors.request.use(async (config) => {
  let token = getDashboardToken();

  // If token exists but is expired/about to expire, refresh it first
  if (token && isSessionTokenExpired()) {
    console.log('Token expired or about to expire, refreshing...');
    const refreshed = await refreshSessionToken();
    if (refreshed) {
      token = getDashboardToken(); // get the fresh token
    }
  }

  if (token) {
    if (!config.headers) {
      config.headers = {} as any;
    }
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: if a 401 comes back, try refreshing token and retry once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only retry once and only for 401s when we have a token
    if (
      error.response?.status === 401 &&
      !originalRequest._retried &&
      getDashboardToken()
    ) {
      originalRequest._retried = true;
      const refreshed = await refreshSessionToken();
      if (refreshed) {
        // Update the header with the new token and retry
        const newToken = getDashboardToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// Forms API - No Authentication
// ============================================

export interface GeneratedPDF {
  formName: string;
  fileName: string;
  blobUrl: string;
  downloadUrl: string;
  generatedAt: string;
  pageCount: number;
}

export interface GenerateResult {
  success: boolean;
  submissionId: string;
  generatedPDFs: GeneratedPDF[];
  message: string;
  error?: string;
  validationErrors?: Array<{ field: string; message: string }>;
}

export interface SubmitResult {
  success: boolean;
  submissionId: string;
  message: string;
  savedPDFs: GeneratedPDF[];
}

export interface AccessSessionResult {
  success: boolean;
  submissionId: string;
  mode: 'signature' | 'full';
  formData: FormSubmission | null;
  signatureStatus?: 'not_signed' | 'signed_complete';
  error?: string;
}

export const formsAPI = {
  /**
   * Send share email with link
   */
  shareLink: async (email: string, link: string): Promise<{ success: boolean; message?: string }> => {
    const response = await api.post('/share', { email, link });
    return response.data;
  },

  /**
   * Generate all PDFs without saving to database
   * Returns preview URLs
   */
  generatePDFs: async (data: FormSubmission, language: 'en' | 'es' = 'en'): Promise<GenerateResult> => {
    const editId = data.submissionId;
    const url = editId ? `/forms/generate?editSubmissionId=${encodeURIComponent(editId)}` : '/forms/generate';
    const response = await api.post<GenerateResult>(url, { ...data, language });
    return response.data;
  },

  /**
   * Get preview URL for a PDF
   */
  getPreviewUrl: (submissionId: string, fileName: string): string => {
    return `${API_BASE_URL}/forms/preview/${submissionId}/${fileName}`;
  },

  /**
   * Get download URL for a PDF
   */
  getDownloadUrl: (submissionId: string, fileName: string): string => {
    return `${API_BASE_URL}/forms/download/${submissionId}/${fileName}`;
  },

  /**
   * Submit and save to database
   */
  submitForms: async (submissionId: string, onBehalfOf?: { userId: string | number; userName: string }): Promise<SubmitResult> => {
    const response = await api.post<SubmitResult>(`/forms/submit/${submissionId}`, { onBehalfOf });
    return response.data;
  },

  /**
   * Get all submissions
   */
  getSubmissions: async (page: number = 1, limit: number = 20) => {
    const response = await api.get(`/forms/submissions?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Get single submission
   */
  getSubmission: async (submissionId: string) => {
    const response = await api.get(`/forms/submissions/${submissionId}`);
    return response.data;
  },

  sendSubmissionLink: async (
    submissionId: string,
    mode: 'signature' | 'full',
    email?: string,
    skipEmail?: boolean
  ): Promise<{ success: boolean; emailSent: boolean; accessLink: string; error?: string }> => {
    const response = await api.post(`/forms/submissions/${submissionId}/send-link`, { mode, email, skipEmail });
    return response.data;
  },

  getAccessSession: async (token: string): Promise<AccessSessionResult> => {
    const response = await api.get(`/forms/access/${encodeURIComponent(token)}`);
    return response.data;
  },

  saveDraft: async (formData: Partial<FormSubmission>, currentStep: number, draftId?: string, onBehalfOf?: { userId: string | number; userName: string }): Promise<{ success: boolean; draftId: string }> => {
    const response = await api.post('/forms/drafts', { formData, currentStep, draftId, onBehalfOf });
    return response.data;
  },

  /**
   * Get approved staff users (for admin "on behalf of" dropdown)
   */
  getApprovedStaffUsers: async (): Promise<{ success: boolean; users: Array<{ id: number; firstName: string; lastName: string; email: string; role: string }> }> => {
    const response = await api.get('/auth/admin/users?status=approved');
    return response.data;
  },

  getDrafts: async (page: number = 1, limit: number = 20) => {
    const response = await api.get(`/forms/drafts?page=${page}&limit=${limit}`);
    return response.data;
  },

  deleteDraft: async (draftId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/forms/drafts/${draftId}`);
    return response.data;
  },

  sendPatientLink: async (
    submissionId: string,
    channel: 'email' | 'sms',
    recipient: string
  ): Promise<{ success: boolean; sent: boolean; accessLink: string; error?: string }> => {
    const response = await api.post(`/forms/submissions/${submissionId}/send-patient-link`, { channel, recipient });
    return response.data;
  },
};

// ============================================
// Local Storage for Auto-save
// ============================================

const AUTOSAVE_PREFIX = 'hipaa-local-draft:';
const AUTOSAVE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// Legacy single-slot key — kept only for migration cleanup
const AUTOSAVE_KEY = 'hipaa-form-autosave';

export interface LocalDraft {
  draftKey: string;
  data: Partial<FormSubmission>;
  savedAt: string;
}

export const localStorageAPI = {
  // Save draft using a stable per-session key. Pass the same key each time to update in place.
  saveFormProgress: (data: Partial<FormSubmission>, draftKey?: string): string => {
    try {
      const key = draftKey || `${AUTOSAVE_PREFIX}${Date.now()}`;
      const saveData = { data, savedAt: new Date().toISOString() };
      localStorage.setItem(key, JSON.stringify(saveData));
      return key;
    } catch (error) {
      console.error('Failed to save form progress:', error);
      return draftKey || '';
    }
  },

  // Remove one specific draft by key (call on successful PDF generation)
  clearFormProgress: (draftKey?: string): void => {
    if (draftKey) {
      localStorage.removeItem(draftKey);
    } else {
      // Fallback: clear legacy single-slot key
      localStorage.removeItem(AUTOSAVE_KEY);
    }
  },

  // List all local drafts, auto-expiring anything older than 7 days
  getAllDrafts: (): LocalDraft[] => {
    const drafts: LocalDraft[] = [];
    // Migrate legacy single-slot key if present
    const legacy = localStorage.getItem(AUTOSAVE_KEY);
    if (legacy) {
      try {
        const parsed = JSON.parse(legacy);
        const newKey = `${AUTOSAVE_PREFIX}${new Date(parsed.savedAt || Date.now()).getTime()}`;
        localStorage.setItem(newKey, legacy);
      } catch { /* ignore */ }
      localStorage.removeItem(AUTOSAVE_KEY);
    }
    // Collect keys first to avoid index shifting during removal
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(AUTOSAVE_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const age = Date.now() - new Date(parsed.savedAt).getTime();
        if (age > AUTOSAVE_TTL_MS) {
          localStorage.removeItem(key);
          continue;
        }
        drafts.push({ draftKey: key, data: parsed.data, savedAt: parsed.savedAt });
      } catch { /* skip corrupt entries */ }
    }
    return drafts.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  },

  hasFormProgress: (): boolean => {
    return localStorageAPI.getAllDrafts().length > 0;
  },

  // Keep for backwards compat — loads the most recent draft
  loadFormProgress: (): { data: Partial<FormSubmission>; savedAt: string } | null => {
    const drafts = localStorageAPI.getAllDrafts();
    return drafts.length > 0 ? { data: drafts[0].data, savedAt: drafts[0].savedAt } : null;
  },
};

export default api;

